import { FlaskConical } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTenantId } from '@/auth/useAuth';
import { Badge } from '@/components/ui/card';
import { useTenant } from './api';

/**
 * Warning shown while a tenant is in test mode (ADR 0024): outbound messages go only to its test recipients.
 * Renders nothing when the count is 0.
 */
export function TestModeBadge({ count, compactOnMobile = false }: { count: number | null | undefined; compactOnMobile?: boolean }) {
  const { t } = useTranslation();
  if (!count || count <= 0) return null;
  return (
    <Badge
      variant="warning"
      className="gap-1 whitespace-nowrap font-medium"
      title={t('admin.testMode.tooltip')}
      aria-label={t('admin.testMode.badge', { count })}
      data-testid="test-mode-badge"
    >
      <FlaskConical className="size-3.5" aria-hidden />
      <span className={compactOnMobile ? 'hidden sm:inline' : undefined}>{t('admin.testMode.badge', { count })}</span>
    </Badge>
  );
}

/** Top-bar variant: the test-mode warning for the tenant the user currently works in. */
export function CurrentTenantTestModeBadge() {
  const tenantId = useTenantId();
  return tenantId ? <TenantTestModeBadge tenantId={tenantId} /> : null;
}

function TenantTestModeBadge({ tenantId }: { tenantId: string }) {
  const tenant = useTenant(tenantId, { silent: true });
  return <TestModeBadge count={tenant.data?.testRecipientCount} compactOnMobile />;
}
